/**
 * Health & Therapeutic Target Categories
 * Provides standardized alphabetical categories, metadata styling,
 * and inference helpers for compound health targets.
 */

export const HEALTH_CATEGORIES = [
  'Cardiovascular Health',
  'Cognitive, Energy & Focus',
  'General or Other Health',
  'Hormone Support',
  'Immune Support',
  'Longevity & Cellular Health',
  'Metabolic Health',
  'Mood & Stress',
  'Skin, Hair & Eye Health',
  'Sleep & Circadian',
  'Strength & Recovery'
];

export const HEALTH_CATEGORY_CONFIG = {
  'Cardiovascular Health': {
    color: 'rose',
    badgeClass: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
    dotClass: 'bg-rose-400',
    description: 'Heart, circulation, lipid profiles, and endothelial vascular function'
  },
  'Cognitive, Energy & Focus': {
    color: 'amber',
    badgeClass: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
    dotClass: 'bg-amber-400',
    description: 'Nootropics, neurotransmitters, focus, memory, and cognitive stamina'
  },
  'General or Other Health': {
    color: 'slate',
    badgeClass: 'bg-slate-500/15 text-slate-300 border border-slate-500/30',
    dotClass: 'bg-slate-400',
    description: 'General wellness, baseline supplementation, and non-classified targets'
  },
  'Hormone Support': {
    color: 'orange',
    badgeClass: 'bg-orange-500/15 text-orange-300 border border-orange-500/30',
    dotClass: 'bg-orange-400',
    description: 'Endocrine health, TRT, androgen modulation, and hormone optimization'
  },
  'Immune Support': {
    color: 'teal',
    badgeClass: 'bg-teal-500/15 text-teal-300 border border-teal-500/30',
    dotClass: 'bg-teal-400',
    description: 'Innate/adaptive immunity, mucosal barriers, and pathogen defense'
  },
  'Longevity & Cellular Health': {
    color: 'cyan',
    badgeClass: 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30',
    dotClass: 'bg-cyan-400',
    description: 'NAD+ enhancement, mitochondrial health, methylation, and autophagy'
  },
  'Metabolic Health': {
    color: 'emerald',
    badgeClass: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    dotClass: 'bg-emerald-400',
    description: 'GLP-1/GIP agonists, insulin sensitivity, AMPK, and glucose management'
  },
  'Mood & Stress': {
    color: 'violet',
    badgeClass: 'bg-violet-500/15 text-violet-300 border border-violet-500/30',
    dotClass: 'bg-violet-400',
    description: 'Cortisol regulation, anxiety reduction, mood balance, and relaxation'
  },
  'Skin, Hair & Eye Health': {
    color: 'pink',
    badgeClass: 'bg-pink-500/15 text-pink-300 border border-pink-500/30',
    dotClass: 'bg-pink-400',
    description: 'Collagen synthesis, tissue repair, hair follicle preservation, and ocular protection'
  },
  'Sleep & Circadian': {
    color: 'indigo',
    badgeClass: 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30',
    dotClass: 'bg-indigo-400',
    description: 'Sleep architecture, slow-wave deep sleep, sleep latency, and circadian rhythms'
  },
  'Strength & Recovery': {
    color: 'lime',
    badgeClass: 'bg-lime-500/15 text-lime-300 border border-lime-500/30',
    dotClass: 'bg-lime-400',
    description: 'Musculoskeletal power, growth hormone secretion, joint health, and workout recovery'
  }
};

/**
 * Intelligent fallback inference for protocols lacking explicit categories.
 * Evaluates compound name and intended outcome strings.
 */
export function inferHealthCategories(protocol = {}) {
  const name = (protocol.name || '').toLowerCase();
  const outcome = (protocol.intendedOutcome || '').toLowerCase();
  const text = `${name} ${outcome}`;

  // Metabolic Health
  if (/tirzepatide|retatrutide|cagrilintide|semaglutide|berberine|metformin|glucagon|glp|gip|insulin|glucose|fat loss|weight/i.test(text)) {
    return {
      primaryCategory: 'Metabolic Health',
      secondaryCategory: /cardio|endothelial|heart|lipid/i.test(text) ? 'Cardiovascular Health' : null
    };
  }

  // Sleep & Circadian
  if (/dsip|melatonin|valerian|tart cherry|sleep|circadian|insomnia/i.test(text)) {
    return {
      primaryCategory: 'Sleep & Circadian',
      secondaryCategory: /relaxation|mood|anxiety|gaba/i.test(text)
        ? 'Mood & Stress'
        : /muscle|joint|recovery/i.test(text)
        ? 'Strength & Recovery'
        : null
    };
  }

  // Magnesium Glycinate
  if (/magnesium/i.test(text)) {
    return {
      primaryCategory: 'Sleep & Circadian',
      secondaryCategory: 'Strength & Recovery'
    };
  }

  // GHK-Cu
  if (/ghk|copper peptide/i.test(text)) {
    return {
      primaryCategory: 'Skin, Hair & Eye Health',
      secondaryCategory: 'Strength & Recovery'
    };
  }

  // Astaxanthin
  if (/astaxanthin/i.test(text)) {
    return {
      primaryCategory: 'Skin, Hair & Eye Health',
      secondaryCategory: 'Longevity & Cellular Health'
    };
  }

  // Hormone Support
  if (/testosterone|boron|dim|saw palmetto|enanthate|cypionate|trt|shbg|estrogen|dht|prostate|aromatase|androgen/i.test(text)) {
    return {
      primaryCategory: 'Hormone Support',
      secondaryCategory: /hair|follicle|skin/i.test(text)
        ? 'Skin, Hair & Eye Health'
        : /muscle|strength/i.test(text)
        ? 'Strength & Recovery'
        : null
    };
  }

  // Strength & Recovery
  if (/creatine|sermorelin|cjc|ipamorelin|ghrp|bpc|tb-500|power|hypertrophy|growth hormone|secretagogue|recovery/i.test(text)) {
    return {
      primaryCategory: 'Strength & Recovery',
      secondaryCategory: /cognitive|focus|atp/i.test(text)
        ? 'Cognitive, Energy & Focus'
        : /gh|hormone/i.test(text)
        ? 'Hormone Support'
        : null
    };
  }

  // Cognitive, Energy & Focus
  if (/alpha gpc|cogitilide|cerebrolysin|choline|racetam|nootropic|cogniti|neuro|focus|memory|brain/i.test(text)) {
    return {
      primaryCategory: 'Cognitive, Energy & Focus',
      secondaryCategory: /energy|atp|mitochondria/i.test(text) ? 'Longevity & Cellular Health' : null
    };
  }

  // Mood & Stress
  if (/lithium|theanine|ashwagandha|mood|calm|anxiety|stress|cortisol|gaba/i.test(text)) {
    return {
      primaryCategory: 'Mood & Stress',
      secondaryCategory: /focus|clarity/i.test(text) ? 'Cognitive, Energy & Focus' : null
    };
  }

  // Cardiovascular Health
  if (/algae oil|omega|epa|dha|tadalafil|citrulline|cardio|vascular|endothelial|blood pressure|nitric oxide/i.test(text)) {
    return {
      primaryCategory: 'Cardiovascular Health',
      secondaryCategory: /pump|workout/i.test(text) ? 'Strength & Recovery' : null
    };
  }

  // Longevity & Cellular Health
  if (/nmn|tmg|nad\+|resveratrol|metformin|quercetin|fisetin|mitochondria|longevity|methylation|cellular/i.test(text)) {
    return {
      primaryCategory: 'Longevity & Cellular Health',
      secondaryCategory: /cardio|homocysteine/i.test(text)
        ? 'Cardiovascular Health'
        : /energy/i.test(text)
        ? 'Cognitive, Energy & Focus'
        : null
    };
  }

  // Immune Support
  if (/zinc|vitamin d|d3|k2|elderberry|glutathione|immune|immunity|pathogen/i.test(text)) {
    return {
      primaryCategory: 'Immune Support',
      secondaryCategory: /bone|calcium/i.test(text) ? 'Hormone Support' : null
    };
  }

  return {
    primaryCategory: 'General or Other Health',
    secondaryCategory: null
  };
}

/**
 * Returns protocol's explicit primaryCategory or infers a fallback.
 */
export function getEffectivePrimaryCategory(protocol = {}) {
  if (protocol.primaryCategory && HEALTH_CATEGORIES.includes(protocol.primaryCategory)) {
    return protocol.primaryCategory;
  }
  return inferHealthCategories(protocol).primaryCategory || 'General or Other Health';
}

/**
 * Returns protocol's explicit secondaryCategory or infers a fallback if applicable.
 */
export function getEffectiveSecondaryCategory(protocol = {}) {
  if (protocol.secondaryCategory && HEALTH_CATEGORIES.includes(protocol.secondaryCategory)) {
    return protocol.secondaryCategory;
  }
  // If explicitly set to null/empty string, respect it
  if (protocol.secondaryCategory === null || protocol.secondaryCategory === '') {
    return null;
  }
  const inferred = inferHealthCategories(protocol);
  // Do not duplicate primary category
  if (inferred.secondaryCategory && inferred.secondaryCategory !== getEffectivePrimaryCategory(protocol)) {
    return inferred.secondaryCategory;
  }
  return null;
}

/**
 * Retrieve badge styling and metadata for a category
 */
export function getCategoryConfig(categoryName) {
  return HEALTH_CATEGORY_CONFIG[categoryName] || HEALTH_CATEGORY_CONFIG['General or Other Health'];
}
